import { useState, useEffect, useRef, useEffectEvent } from 'react';
import axios from 'axios';
import { theme, typography, spacing, borderRadius } from '../styles/theme';
import * as Sentry from '@sentry/react';
import { exportConversationToPDF } from '../utils/exportPDF';

export default function ConversationSidebar({ 
  user, 
  currentConversationId,
  onSelectConversation, 
  onNewConversation,
  isOpen,
  onClose,
  refreshTrigger
}) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFavorites, setShowFavorites] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  // Fetch conversations
  useEffect(() => {
    if (!user) return;
    fetchConversations();
  }, [user, showFavorites, refreshTrigger]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (showFavorites) params.append('favorites', 'true');

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/conversations?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      Sentry.captureException(error, {
        tags: { component: 'ConversationSidebar', action: 'fetchConversations' }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchConversations();
  };

  const handleDelete = async (convId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;

    try {
      const token = await user.getIdToken();
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/conversations/${convId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(prev => prev.filter(c => c._id !== convId));
      if (currentConversationId === convId) {
        onNewConversation();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      Sentry.captureException(error, {
        tags: { component: 'ConversationSidebar', action: 'deleteConversation' }
      });
    }
  };

  const handleToggleFavorite = async (convId, e) => {
    e.stopPropagation();
    try {
      const token = await user.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/conversations/${convId}/favorite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations(prev => prev.map(c => 
        c._id === convId ? { ...c, isFavorite: response.data.isFavorite } : c
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
      Sentry.captureException(error, {
        tags: { component: 'ConversationSidebar', action: 'toggleFavorite' }
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const handleStartRename = (conv, e) => {
    e.stopPropagation();
    setEditingId(conv._id);
    setEditTitle(conv.title);
  };

  const handleSaveRename = async (convId, e) => {

    if (!editTitle.trim()) return;

    try {
        const token = await user.getIdToken();
        await axios.put(
            `${process.env.REACT_APP_API_URL}/api/conversations/${convId}`,
            { title: editTitle },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        setConversations(prev => prev.map(c =>
            c._id === convId ? { ...c, title: editTitle } : c
        ));
        setEditingId(null);
    } catch (error) {
        console.error('Error renaming conversation:', error)
        Sentry.captureException(error, {
        tags: { component: 'ConversationSidebar', action: 'renameConversation' }
      });
    }
  };

  const handleCancelRename = (e) => {
    e.stopPropagation();
    setEditingId(null);
    setEditTitle('');
  };

  const handleExportConversation = async (conv, e) => {
    e.stopPropagation();
    
    try {
      // Load full conversation
      const token = await user.getIdToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/conversations/${conv._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Convert to message format for export
      const messages = response.data.messages.map(msg => ({
        sender: msg.role === 'user' ? 'user' : 'bot',
        text: msg.content
      }));
      
      exportConversationToPDF(messages, conv.title);
    } catch (error) {
      console.error('Error exporting conversation:', error);
      Sentry.captureException(error, {
        tags: { component: 'ConversationSidebar', action: 'exportConversation' }
      });
      alert('Failed to export conversation');
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      width: '280px',
      height: '100%',
      backgroundColor: theme.bg.secondary,
      borderRight: `1px solid ${theme.border.light}`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: spacing.md,
        paddingTop: spacing.lg,
        borderBottom: `1px solid ${theme.border.light}`,
      }}>
        <button
          onClick={onNewConversation}
          style={{
            width: '100%',
            padding: spacing.md,
            backgroundColor: theme.brand.primary,
            color: 'white',
            border: 'none',
            borderRadius: borderRadius.md,
            cursor: 'pointer',
            fontWeight: typography.fontWeight.medium,
            fontSize: typography.fontSize.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.sm,
          }}
        >
          + New Conversation
        </button>
      </div>

      {/* Search */}
      <div style={{ padding: spacing.md }}>
        <form onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: spacing.sm,
              border: `1px solid ${theme.border.light}`,
              borderRadius: borderRadius.sm,
              fontSize: typography.fontSize.sm,
              boxSizing: 'border-box',
            }}
          />
        </form>
        
        {/* Favorites filter */}
        <button
          onClick={() => setShowFavorites(!showFavorites)}
          style={{
            marginTop: spacing.sm,
            padding: `${spacing.xs} ${spacing.sm}`,
            backgroundColor: showFavorites ? theme.brand.primary : 'transparent',
            color: showFavorites ? 'white' : theme.text.secondary,
            border: `1px solid ${theme.border.light}`,
            borderRadius: borderRadius.sm,
            cursor: 'pointer',
            fontSize: typography.fontSize.xs,
          }}
        >
          ⭐ Favorites
        </button>
      </div>

      {/* Conversation List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: `0 ${spacing.sm}`,
      }}>
        {loading ? (
          <div style={{ 
            padding: spacing.lg, 
            textAlign: 'center',
            color: theme.text.secondary 
          }}>
            Loading...
          </div>
        ) : conversations.length === 0 ? (
          <div style={{ 
            padding: spacing.lg, 
            textAlign: 'center',
            color: theme.text.secondary,
            fontSize: typography.fontSize.sm,
          }}>
            {showFavorites ? 'No favorites yet' : 'No conversations yet'}
          </div>
        ) : (
          conversations.map(conv => (
            <div
              key={conv._id}
              onClick={() => onSelectConversation(conv._id)}
              style={{
                padding: spacing.md,
                marginBottom: spacing.xs,
                backgroundColor: currentConversationId === conv._id 
                  ? theme.brand.primary 
                  : 'transparent',
                borderRadius: borderRadius.md,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                if (currentConversationId !== conv._id) {
                  e.currentTarget.style.backgroundColor = theme.bg.tertiary;
                }
              }}
              onMouseLeave={(e) => {
                if (currentConversationId !== conv._id) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Title - editable or display */}
                  {editingId === conv._id ? (
                    <div style={{ display: 'flex', gap: spacing.xs }} onClick={e => e.stopPropagation()}>
                        <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveRename(conv._id, e);
                                if (e.key === 'Escape') handleCancelRename(e);
                            }}
                            autoFocus
                            style={{
                                flex: 1,
                                padding: spacing.xs,
                                fontSize: typography.fontSize.sm,
                                border: `1px solid ${theme.border.light}`,
                                borderRadius: borderRadius.sm,
                            }}
                        />
                        <button
                            onClick={(e) => handleSaveRename(conv._id, e)}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: spacing.xs,
                                color: theme.status.success,
                            }}
                        >
                            ✓
                        </button>
                        <button
                            onClick={handleCancelRename}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: spacing.xs,
                                color: theme.status.error,
                            }}
                        >
                            x
                        </button>
                    </div>
                  ) : (
                    <div style={{
                        fontSize: typography.fontSize.sm,
                        fontWeight: typography.fontWeight.medium,
                        color: theme.text.primary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {conv.isFavorite && '⭐ '}{conv.title}
                    </div>
                  )}
                  <div style={{
                    fontSize: typography.fontSize.xs,
                    color: theme.text.tertiary,
                    marginTop: spacing.xs,
                  }}>
                    {formatDate(conv.updatedAt)} · {conv.messageCount} messages
                  </div>
                </div>
                
                {/* Meatball menu - only show if not editing */}
                {editingId !== conv._id && (
                  <div style={{ position: 'relative' }} ref={openMenuId === conv._id ? menuRef : null}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === conv._id ? null : conv._id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: spacing.xs,
                        fontSize: typography.fontSize.lg,
                        color: theme.text.secondary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '24px',
                        minHeight: '24px',
                      }}
                      title="Options"
                    >
                      ⋮
                    </button>

                    {/* Dropdown menu */}
                    {openMenuId === conv._id && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          backgroundColor: 'white',
                          border: `1px solid ${theme.border.light}`,
                          borderRadius: borderRadius.md,
                          boxShadow: theme.shadow.medium,
                          zIndex: 1000,
                          minWidth: '160px',
                          marginTop: spacing.xs,
                        }}
                      >
                        {/* Rename */}
                        <button
                          onClick={(e) => {
                            handleStartRename(conv, e);
                            setOpenMenuId(null);
                          }}
                          style={{
                            width: '100%',
                            padding: `${spacing.sm} ${spacing.md}`,
                            border: 'none',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: typography.fontSize.sm,
                            color: theme.text.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.sm,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.bg.secondary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span>✏️</span>
                          <span>Rename</span>
                        </button>

                        {/* Favorite/Unfavorite */}
                        <button
                          onClick={(e) => {
                            handleToggleFavorite(conv._id, e);
                            setOpenMenuId(null);
                          }}
                          style={{
                            width: '100%',
                            padding: `${spacing.sm} ${spacing.md}`,
                            border: 'none',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: typography.fontSize.sm,
                            color: theme.text.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.sm,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.bg.secondary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span>{conv.isFavorite ? '⭐' : '☆'}</span>
                          <span>{conv.isFavorite ? 'Unfavorite' : 'Favorite'}</span>
                        </button>

                        {/* Export to PDF */}
                        <button
                          onClick={(e) => {
                            handleExportConversation(conv, e);
                            setOpenMenuId(null);
                          }}
                          style={{
                            width: '100%',
                            padding: `${spacing.sm} ${spacing.md}`,
                            border: 'none',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: typography.fontSize.sm,
                            color: theme.text.primary,
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.sm,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.bg.secondary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span>📄</span>
                          <span>Export PDF</span>
                        </button>

                        {/* Divider */}
                        <div style={{
                          height: '1px',
                          backgroundColor: theme.border.light,
                          margin: `${spacing.xs} 0`,
                        }} />

                        {/* Delete */}
                        <button
                          onClick={(e) => {
                            handleDelete(conv._id, e);
                            setOpenMenuId(null);
                          }}
                          style={{
                            width: '100%',
                            padding: `${spacing.sm} ${spacing.md}`,
                            border: 'none',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: typography.fontSize.sm,
                            color: theme.status.error,
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing.sm,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFE5E5';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span>🗑</span>
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
            </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}